import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, ExternalLink, LogIn } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { cn } from '@/lib/utils'

interface NavItem {
  label: string
  href: string
  external?: boolean
  children?: { label: string; href: string }[]
}

export function Header() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const isLoginPage = location.pathname.includes('/login') || location.pathname.includes('/accedi')
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [expandedDropdown, setExpandedDropdown] = useState<string | null>(null)
  const isDarkStyle = isScrolled || isLoginPage

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  const navItems: NavItem[] = [
    {
      label: t('nav.home'),
      href: '/',
    },
    {
      label: t('nav.company'),
      href: '/company',
    },
    {
      label: t('nav.shop'),
      href: 'https://shop.noberasco1908.it',
      external: true,
    },
    {
      label: t('nav.products'),
      href: '/products',
    },
    {
      label: t('nav.recipes'),
      href: '/recipes',
    },
    {
      label: t('nav.sustainability'),
      href: '/sustainability',
    },
    {
      label: t('nav.contacts'),
      href: '/contacts',
    },
  ]

  const toggleDropdown = (label: string) => {
    setExpandedDropdown(expandedDropdown === label ? null : label)
  }

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-sticky transition-all duration-normal',
          isDarkStyle
            ? 'bg-white/95 backdrop-blur-md shadow-sm py-2'
            : 'bg-transparent py-4'
        )}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <LocalizedLink
              to="/"
              className={cn(
                'flex items-center gap-2 transition-all duration-normal',
                isDarkStyle ? 'h-10' : 'h-12'
              )}
            >
              <img
                src="/media/images/logos/Logo.png"
                alt="Noberasco"
                className={cn(
                  'h-full w-auto object-contain transition-all duration-normal',
                  isDarkStyle ? 'h-10' : 'h-12'
                )}
              />
            </LocalizedLink>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <div key={item.label} className="relative">
                  {item.children ? (
                    <div className="relative">
                      <button
                        onClick={() => toggleDropdown(item.label)}
                        className={cn(
                          'flex items-center gap-1 px-4 py-2 font-medium transition-colors duration-normal rounded-lg',
                          isDarkStyle
                            ? 'text-gray-700 hover:text-primary hover:bg-primary/5'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                        )}
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 transition-transform duration-fast',
                            expandedDropdown === item.label ? 'rotate-180' : ''
                          )}
                        />
                      </button>

                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {expandedDropdown === item.label && (
                          <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2"
                          >
                            {item.children.map((child) => (
                              <LocalizedLink
                                key={child.href}
                                to={child.href}
                                className="block px-4 py-2 text-gray-700 hover:text-primary hover:bg-primary/5 transition-colors"
                                onClick={() => setExpandedDropdown(null)}
                              >
                                {child.label}
                              </LocalizedLink>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        'flex items-center gap-1.5 px-4 py-2 font-medium transition-colors duration-normal rounded-lg',
                        isDarkStyle
                          ? 'text-gray-700 hover:text-primary hover:bg-primary/5'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      )}
                    >
                      {item.label}
                      <ExternalLink className="w-3.5 h-3.5 opacity-50" />
                    </a>
                  ) : (
                    <LocalizedLink
                      to={item.href}
                      className={cn(
                        'px-4 py-2 font-medium transition-colors duration-normal rounded-lg',
                        isDarkStyle
                          ? 'text-gray-700 hover:text-primary hover:bg-primary/5'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      )}
                    >
                      {item.label}
                    </LocalizedLink>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side: Login + Language Selector + Mobile Menu Button */}
            <div className="flex items-center gap-4">
              <LocalizedLink
                to="login"
                className={cn(
                  'hidden lg:inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md border transition-all duration-200',
                  isDarkStyle
                    ? 'border-primary text-primary hover:bg-primary hover:text-white'
                    : 'border-white/50 text-white hover:bg-white/10 hover:border-white'
                )}
              >
                <LogIn className="w-3.5 h-3.5" />
                {t('auth.signIn')}
              </LocalizedLink>

              <div className="hidden lg:block">
                <LanguageSelector variant="desktop" isScrolled={isDarkStyle} />
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={cn(
                  'lg:hidden p-2 rounded-lg transition-colors',
                  isDarkStyle
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                )}
                aria-label={isMobileMenuOpen ? t('aria.closeMenu') : t('aria.openMenu')}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-sticky lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white z-sticky lg:hidden overflow-y-auto"
            >
              <div className="flex flex-col h-full">
                {/* Mobile Menu Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <LocalizedLink
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="h-10"
                  >
                    <img
                      src="/media/images/logos/Logo.png"
                      alt="Noberasco"
                      className="h-full w-auto"
                    />
                  </LocalizedLink>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    aria-label={t('aria.closeMenu')}
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Mobile Navigation */}
                <nav className="flex-1 py-4">
                  {navItems.map((item) => (
                    <div key={item.label}>
                      {item.children ? (
                        <div className="border-b border-gray-50">
                          <button
                            onClick={() => toggleDropdown(item.label)}
                            className="flex items-center justify-between w-full px-6 py-4 text-gray-900 font-medium"
                          >
                            {item.label}
                            <ChevronDown
                              className={cn(
                                'w-5 h-5 transition-transform duration-fast',
                                expandedDropdown === item.label ? 'rotate-180' : ''
                              )}
                            />
                          </button>

                          {/* Accordion Content */}
                          <AnimatePresence>
                            {expandedDropdown === item.label && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="bg-gray-50 py-2">
                                  {item.children.map((child) => (
                                    <LocalizedLink
                                      key={child.href}
                                      to={child.href}
                                      className="block px-8 py-3 text-gray-600 hover:text-primary hover:bg-primary/5 transition-colors"
                                      onClick={() => {
                                        setIsMobileMenuOpen(false)
                                        setExpandedDropdown(null)
                                      }}
                                    >
                                      {child.label}
                                    </LocalizedLink>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : item.external ? (
                        <div className="border-b border-gray-50">
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-6 py-4 text-gray-900 font-medium hover:text-primary hover:bg-primary/5 transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                            <ExternalLink className="w-4 h-4 opacity-40" />
                          </a>
                        </div>
                      ) : (
                        <div className="border-b border-gray-50">
                          <LocalizedLink
                            to={item.href}
                            className="block px-6 py-4 text-gray-900 font-medium hover:text-primary hover:bg-primary/5 transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </LocalizedLink>
                        </div>
                      )}
                    </div>
                  ))}
                </nav>

                {/* Mobile Login + Language Selector */}
                <div className="p-4 border-t border-gray-100 space-y-3">
                  <LocalizedLink
                    to="login"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition-colors w-full"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LogIn className="w-4 h-4" />
                    {t('auth.signIn')}
                  </LocalizedLink>
                  <LanguageSelector variant="mobile" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
